import { supabase } from "@/lib/supabaseClient";
import type { PropertyType, MembershipRole } from "@/lib/supabase/types";

/**
 * Onboarding funnels through the SECURITY DEFINER RPC
 * `diyafa_onboard_company` so the first-ever org INSERT bypasses the
 * org-members RLS policy (which can't see a user whose session is
 * pending email confirmation).
 *
 * Flow:
 *   1. Client calls supabase.auth.signUp(email, password)
 *      → returns authData.user.id.
 *   2. Client calls RPC diyafa_onboard_company(p_user_id, ...) which
 *      atomically inserts organizations → properties → owner membership
 *      in a single transaction, elevated past RLS.
 *   3. For each invite, RPC diyafa_add_member_invite writes a row into
 *      the dedicated `invitations` table (migration 007). Invites are
 *      redeemed later via diyafa_accept_invitation(token).
 *
 * Note on email confirmation:
 *   Migration 005 auto-confirms every new auth.users row at INSERT time,
 *   so the session is usable immediately. Supabase email-confirm toggle
 *   stays ON at the project level — we just pre-satisfy it.
 */

export interface OnboardingInput {
  crNumber?: string;          // from the CR lookup, stored for audit/future MOCIIP pairing
  company: {
    name: string;
    name_ar?: string;
    phone?: string;
    email: string;
    address?: string;
    sector?: string;
    country_code?: string;
  };
  firstProperty: {
    type: PropertyType;
    name: string;
  };
  owner: {
    password: string;
    fullName?: string;
  };
  invites: Array<{
    email: string;
    role: Exclude<MembershipRole, "owner">;
  }>;
}

export interface OnboardingResult {
  orgId: string;
  propertyId: string;
  membershipId: string;
  inviteCount: number;
  sessionActive: boolean;   // true when Supabase returned a session; false means email-confirm is pending
}

export async function commitOnboarding(input: OnboardingInput): Promise<OnboardingResult> {
  // 1. Create the Supabase auth user.
  const { data: authData, error: authError } = await supabase.auth.signUp({
    email: input.company.email,
    password: input.owner.password,
    options: {
      data: {
        full_name: input.owner.fullName ?? input.company.name,
      },
    },
  });
  if (authError) throw authError;
  const userId = authData.user?.id;
  if (!userId) throw new Error("Supabase returned no user id after signUp");

  // 2. Call the RPC to create org + property + owner membership atomically.
  const { data: rpcData, error: rpcError } = await supabase.rpc("diyafa_onboard_company", {
    p_user_id: userId,
    p_company_name: input.company.name,
    p_company_name_ar: input.company.name_ar ?? null,
    p_company_email: input.company.email,
    p_company_phone: input.company.phone ?? null,
    p_company_address: input.company.address ?? null,
    p_company_sector: input.company.sector ?? null,
    p_cr_number: input.crNumber ?? null,
    p_country_code: "OM",
    p_property_type: input.firstProperty.type,
    p_property_name: input.firstProperty.name,
    p_owner_name: input.owner.fullName ?? null,
  });
  if (rpcError) throw rpcError;

  const result = rpcData as { org_id: string; property_id: string; membership_id: string };

  // 3. Manager invites go into the new `invitations` table (migration
  //    007) — NOT a placeholder membership row. Failures here don't
  //    block onboarding; the owner can re-invite from the dashboard.
  let inviteCount = 0;
  for (const invite of input.invites) {
    const { error: inviteError } = await supabase.rpc("diyafa_add_member_invite", {
      p_org_id: result.org_id,
      p_invited_email: invite.email,
      p_role: invite.role,
    });
    if (!inviteError) inviteCount++;
  }

  return {
    orgId: result.org_id,
    propertyId: result.property_id,
    membershipId: result.membership_id,
    inviteCount,
    sessionActive: !!authData.session,
  };
}
